import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { getLeadTracks } from "@/config/endpoints";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/library/dialog";
import useTracksPipeline from "@/features/stage/api/useTracksPipeline";
import type { EligibleTrackUser } from "@/features/stage/types/stageTypes";
import { cn } from "@/lib/utils";

interface TrackAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: number | string | null;
  roleName?: string;
  /** The role's Own tracks the viewer may hand out (≥ 1). */
  trackIds: number[];
  leadIds: Array<number | string>;
  /** Fired after a successful assignment (close the popup, clear ticks). */
  onDone: () => void;
}

interface BulkResult {
  trackName: string;
  assigned: number;
  skipped: Array<{ leadId: number; reason: string }>;
}

/**
 * Hand the selected lead(s) to a TRACK MEMBER. Opened from the Assign popup
 * when a track-member role (Own on a track) is picked. This is a track
 * assignment — the leads' own assignees are not touched. A lead that is not
 * on the track yet is put on it at the track's first status.
 */
export default function TrackAssignDialog({
  open,
  onOpenChange,
  roleId,
  roleName,
  trackIds,
  leadIds,
  onDone,
}: TrackAssignDialogProps) {
  const queryClient = useQueryClient();
  const { tracks } = useTracksPipeline({ viewable: true });
  const [trackId, setTrackId] = useState<number | null>(null);
  const [picked, setPicked] = useState<number | "auto">("auto");

  useEffect(() => {
    if (!open) return;
    setTrackId(trackIds[0] ?? null);
    setPicked("auto");
    // Re-seed only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const nameOf = (id: number) =>
    (tracks ?? []).find((t: { id: number }) => t.id === id)?.name ??
    `Track #${id}`;
  // A deal-based track hands out each DEAL: the member goes on every deal of
  // the lead on the track, or — when it's on none — on each deal whose stage
  // uses the track (absent grain = an older server = lead based).
  const isPerDeal = (id: number | null) =>
    id != null &&
    (tracks ?? []).find((t: { id: number; grain?: string }) => t.id === id)?.grain === "deal";

  const members = useQuery({
    queryKey: ["track-members", trackId],
    enabled: open && trackId != null,
    queryFn: async (): Promise<EligibleTrackUser[]> => {
      const { data } = await axiosInstance.get(
        `${getLeadTracks}/users/members/${trackId}`
      );
      return data?.data ?? [];
    },
  });
  // Only members of the role that was picked in the popup.
  const list = (members.data ?? []).filter(
    (u) => roleId == null || String(u.roleId) === String(roleId)
  );

  const assign = useMutation({
    mutationFn: async (): Promise<BulkResult> => {
      const { data } = await axiosInstance.post(`${getLeadTracks}/users/bulk`, {
        leadIds: leadIds.map(Number),
        trackId,
        userId: picked,
      });
      return data?.data;
    },
    onSuccess: (res) => {
      if (res.assigned > 0) {
        toast.success(
          `${res.trackName}: assigned ${res.assigned} lead${res.assigned === 1 ? "" : "s"}`
        );
      }
      if (res.skipped.length > 0) {
        toast.warning(
          `${res.skipped.length} skipped — ${res.skipped[0]?.reason ?? ""}`
        );
      }
      for (const key of ["leads", "entity-tracks", "track-followups", "track-dashboard", "track-members"]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      onDone();
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to assign the track");
    },
  });

  const row = (active: boolean) =>
    cn(
      "w-full flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors",
      active
        ? "border-[var(--color-success)] bg-[#edfaf5]"
        : "border-[var(--color-black-1)] bg-white hover:bg-[#f7f9f8]"
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[440px] p-0 gap-0 overflow-hidden rounded-[24px]"
        showCloseButton={true}
      >
        <DialogHeader className="p-[16px] pb-[11px] pt-[15px] border-b border-[#E6E8E7] text-left">
          <DialogTitle className="!text-[18px] leading-normal font-medium text-[#051912] tracking-[-0.72px]">
            Assign {leadIds.length} lead{leadIds.length === 1 ? "" : "s"} to{" "}
            {roleName ?? "track member"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#505E59] tracking-[-0.26px]">
            This assigns the <b>track</b>, not the lead: the member handles it
            and sees these leads in their own list. Leads not on the track yet
            are added at its first status. Current lead assignees stay as they
            are.
          </DialogDescription>
        </DialogHeader>

        <div className="p-[16px] max-h-[52vh] overflow-y-auto flex flex-col gap-3">
          {trackIds.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {trackIds.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setTrackId(id);
                    setPicked("auto");
                  }}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[12px]",
                    trackId === id
                      ? "border-[var(--color-success)] bg-[#edfaf5] text-[#051912]"
                      : "border-[var(--color-black-1)] bg-white text-[#505e59]"
                  )}
                >
                  {nameOf(id)}
                </button>
              ))}
            </div>
          )}
          {trackIds.length === 1 && trackId != null && (
            <p className="text-[12px] text-[#697571]">
              Track: <span className="text-[#051912]">{nameOf(trackId)}</span>
            </p>
          )}
          {isPerDeal(trackId) && (
            <p className="text-[12px] text-[#697571]">
              Per deal: applied to each deal in the track's stages. Leads with
              no such deal are skipped.
            </p>
          )}

          {members.isLoading ? (
            <p className="text-sm text-[#505e59] text-center py-4">
              Loading members…
            </p>
          ) : list.length === 0 ? (
            <p className="text-sm text-[#505e59] text-center py-4">
              No active member in this role.
            </p>
          ) : (
            <>
              <button
                type="button"
                className={row(picked === "auto")}
                onClick={() => setPicked("auto")}
              >
                <span className="text-sm font-medium text-[#051912]">
                  Auto-assign
                </span>
                <span className="text-[12px] text-[#697571]">
                  share between members by turn
                </span>
              </button>
              {list.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className={row(picked === u.id)}
                  onClick={() => setPicked(u.id)}
                >
                  <span className="text-sm font-medium text-[#051912] truncate">
                    {u.name}
                  </span>
                  <span className="text-[12px] text-[#697571] shrink-0">
                    {u.load} active
                  </span>
                </button>
              ))}
            </>
          )}
        </div>

        <DialogFooter className="p-[16px] pt-0 gap-2 sm:justify-end">
          <Button variant="cancel" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              assign.isPending || members.isLoading || list.length === 0
            }
            onClick={() => assign.mutate()}
          >
            {assign.isPending ? "Assigning…" : "Assign track"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
